'use client';
import {useEffect, useRef, useState} from 'react';
import {Volume2, VolumeX} from 'lucide-react';
import {audioContextConstructor} from './browser-capabilities.js';
import {createMusicPlayback} from './music-playback.js';

export default function BackgroundMusic({moon = false, prepareMoon = false}: {moon?: boolean; prepareMoon?: boolean}) {
  const playback = useRef<ReturnType<typeof createMusicPlayback> | null>(null);
  const enabled = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [visible, setVisible] = useState(false);
  const inSpace = useRef(moon);
  const nearSpace = useRef(prepareMoon);
  function play() { void playback.current?.play().catch(() => {}); }
  function stop() { playback.current?.stop(); }
  useEffect(() => { inSpace.current = moon; playback.current?.setScene(moon); }, [moon]);
  useEffect(() => { nearSpace.current = prepareMoon; playback.current?.prepareScene(prepareMoon); }, [prepareMoon]);
  useEffect(() => {
    const Audio = audioContextConstructor();
    if (!Audio) return;
    const mobile = Math.min(innerWidth,innerHeight)<=700 || matchMedia('(pointer: coarse)').matches;
    const player = createMusicPlayback({
      createContext: () => new Audio({latencyHint: mobile ? 'playback' : 'interactive'}),
      fetchAudio: async (url: string) => {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Music unavailable');
        return response.arrayBuffer();
      },
      onPlaying: setPlaying,
      isHidden: () => document.hidden,
    });
    playback.current = player;
    player.setScene(inSpace.current);
    player.prepareScene(nearSpace.current);
    const enter = () => {
      let muted = false;
      try { muted = localStorage.getItem('codex-music-muted') === 'true'; } catch {}
      enabled.current = !muted;
      if (enabled.current) play();
    };
    // The automatic cinematic no longer supplies an entrance-button gesture.
    // Unlock Safari audio on the first real touch/click/key, respecting mute.
    const gesture = (event: Event) => {
      if (!event.isTrusted || (event.target as Element)?.closest?.('.background-music-toggle')) return;
      if (player.isRunning() && enabled.current) return;
      let muted=false;
      try {muted=localStorage.getItem('codex-music-muted') === 'true';} catch {}
      if (muted) return;
      enabled.current=true;
      play();
    };
    const reveal = () => setVisible(true);
    const visibility = () => {
      if (document.hidden) stop();
      else if (enabled.current) play();
    };
    window.addEventListener('codex-prepare-world', enter);
    window.addEventListener('codex-enter-world', reveal);
    document.addEventListener('visibilitychange', visibility);
    document.addEventListener('click', gesture);
    document.addEventListener('touchend', gesture, {passive:true});
    document.addEventListener('keydown', gesture);
    return () => {
      window.removeEventListener('codex-prepare-world', enter);
      window.removeEventListener('codex-enter-world', reveal);
      document.removeEventListener('visibilitychange', visibility);
      document.removeEventListener('click', gesture);
      document.removeEventListener('touchend', gesture);
      document.removeEventListener('keydown', gesture);
      player.dispose();
      playback.current = null;
    };
  }, []);
  if (!visible) return null;
  return <button className="background-music-toggle" data-music-scene={moon ? 'space' : 'environment'} type="button" aria-label={playing ? 'Mute background music' : 'Play background music'} aria-pressed={playing} onClick={() => {
    enabled.current = !playing;
    try { localStorage.setItem('codex-music-muted', String(!enabled.current)); } catch {}
    if (enabled.current) play(); else stop();
  }}>{playing ? <Volume2 size={17}/> : <VolumeX size={17}/>}<span>Sound {playing ? 'on' : 'off'}</span></button>;
}
